const QUICK_BUTTON_SELECTOR = '.st-quick-btn';
const CANDIDATE_TAG_SELECTOR = '#st-candidates-list .st-candidate-tag';

/**
 * Owns the Search Terms quick-selection buttons. The source of truth remains
 * the currently rendered (and therefore filtered) candidate tags: selecting
 * "Top 20" must not silently include candidates the operator has filtered
 * out. It writes the selected set once per click so the reactive Step2
 * subscribers perform one redraw and one Search Terms sync.
 */
export function createSearchTermsQuickSelectionController({
  selectAll,
  getState,
  setState,
} = {}) {
  let activeQuickButton = null;

  function getQuickButtons() {
    return Array.from(selectAll?.(QUICK_BUTTON_SELECTOR) || []);
  }

  function getVisibleKeywords() {
    return Array.from(selectAll?.(CANDIDATE_TAG_SELECTOR) || [])
      .map((tag) => String(tag?.dataset?.keyword || '').trim())
      .filter(Boolean);
  }

  function setSelectionModeToManual() {
    setState?.('stSelectionMode', 'manual');
  }

  function clearActiveButton() {
    activeQuickButton?.classList?.remove('active');
    activeQuickButton = null;
  }

  function choose(button) {
    if (!button) return;
    const count = Number.parseInt(button.dataset?.quickCount, 10);
    if (!Number.isFinite(count) || count < 1) return;

    const quickButtons = getQuickButtons();
    const topKeywords = getVisibleKeywords().slice(0, count);
    const selected = new Set(getState?.('selectedStKeywords') || []);

    // Clicking the active scope removes only the scope it added; manually
    // selected candidates outside that scope stay intact, matching legacy UI.
    if (activeQuickButton === button) {
      topKeywords.forEach((keyword) => selected.delete(keyword));
      clearActiveButton();
      setSelectionModeToManual();
      setState?.('selectedStKeywords', selected);
      return;
    }

    selected.clear();
    topKeywords.forEach((keyword) => selected.add(keyword));
    quickButtons.forEach((item) => item.classList?.remove('active'));
    button.classList?.add('active');
    activeQuickButton = button;
    setSelectionModeToManual();
    setState?.('selectedStKeywords', selected);
  }

  function bind() {
    getQuickButtons().forEach((button) => {
      button.addEventListener('click', () => choose(button));
    });
  }

  return {
    bind,
    choose,
    getVisibleKeywords,
  };
}
